import { baseApi } from './axios';
import {
	CreateSupportTicketRequest,
	AddTicketMessageRequest,
	RateTicketRequest,
	UpdateTicketStatusRequest,
	ReopenTicketRequest,
	EscalateTicketRequest,
	CreateSupportQuestionVoteRequest,
	BaseAPIResponse,
} from 'types';

const baseUrl = `${process.env.REACT_APP_API_URL}/support-question`;
const ticketBaseUrl = `${process.env.REACT_APP_API_URL}/support-ticket`;
const voteBaseUrl = `${process.env.REACT_APP_API_URL}/support-question-vote`;

// Support Questions API
export interface GetSupportQuestionsAPIResponse extends BaseAPIResponse {
	data?: any[];
}

export const getSupportQuestions = async (
	category?: string,
	positionId?: string,
	companyId?: string,
	batchId?: number,
	supportCategory?: string,
	supportSubcategory?: string,
): Promise<GetSupportQuestionsAPIResponse> => {
	const params = new URLSearchParams();
	if (category) params.append('category', category);
	if (positionId) params.append('positionId', positionId);
	if (companyId) params.append('companyId', companyId);
	if (batchId !== undefined) params.append('batchId', batchId.toString());
	if (supportCategory) params.append('supportCategory', supportCategory);
	if (supportSubcategory) params.append('supportSubcategory', supportSubcategory);
	params.append('isActive', 'true');

	const response = await baseApi.get(`${baseUrl}/public/all?${params.toString()}`);
	return response.data;
};

export const getSupportQuestionById = async (id: string): Promise<BaseAPIResponse> => {
	const response = await baseApi.get(`${baseUrl}/public/${id}`);
	return response.data;
};

// Support Tickets API
export interface GetMyTicketsAPIResponse extends BaseAPIResponse {
	data?: any[];
}

export const getMyTickets = async (status?: string, category?: string): Promise<GetMyTicketsAPIResponse> => {
	const params = new URLSearchParams();
	if (status) params.append('status', status);
	if (category) params.append('category', category);

	const response = await baseApi.get(`${ticketBaseUrl}/my-tickets?${params.toString()}`);
	return response.data;
};

export const getTicketById = async (id: string): Promise<BaseAPIResponse> => {
	const response = await baseApi.get(`${ticketBaseUrl}/${id}`);
	return response.data;
};

export const createSupportTicket = async (data: CreateSupportTicketRequest): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${ticketBaseUrl}/create`, data);
	return response.data;
};

export const addTicketMessage = async (
	ticketId: string,
	data: AddTicketMessageRequest,
): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${ticketBaseUrl}/${ticketId}/message`, data);
	return response.data;
};

export const rateTicket = async (id: string, data: RateTicketRequest): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${ticketBaseUrl}/${id}/rate`, data);
	return response.data;
};

export const updateTicketStatus = async (
	id: string,
	data: UpdateTicketStatusRequest,
): Promise<BaseAPIResponse> => {
	const response = await baseApi.patch(`${ticketBaseUrl}/${id}/status`, data);
	return response.data;
};

export const reopenTicket = async (
	id: string,
	data?: ReopenTicketRequest,
): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${ticketBaseUrl}/${id}/reopen`, data || {});
	return response.data;
};

export const escalateTicket = async (
	id: string,
	data?: EscalateTicketRequest,
): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${ticketBaseUrl}/${id}/escalate`, data || {});
	return response.data;
};

export const getAssignedTickets = async (status?: string): Promise<GetMyTicketsAPIResponse> => {
	const params = new URLSearchParams();
	if (status) params.append('status', status);

	const response = await baseApi.get(`${ticketBaseUrl}/assigned-tickets?${params.toString()}`);
	return response.data;
};

export interface GetRedirectCandidatesAPIResponse extends BaseAPIResponse {
	data?: { _id: string; name: string; email: string; role: string }[];
}

export const getRedirectCandidates = async (): Promise<GetRedirectCandidatesAPIResponse> => {
	const response = await baseApi.get(`${ticketBaseUrl}/redirect-candidates`);
	return response.data;
};

export const redirectTicket = async (
	id: string,
	data: { redirectToUserId: string; reason?: string },
): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${ticketBaseUrl}/${id}/redirect`, data);
	return response.data;
};

// Support Question Vote API
export interface GetVotesAPIResponse extends BaseAPIResponse {
	data?: {
		votes: any[];
		upvotes: number;
		downvotes: number;
		total: number;
	};
}

export const voteOnQuestion = async (data: CreateSupportQuestionVoteRequest): Promise<BaseAPIResponse> => {
	const response = await baseApi.post(`${voteBaseUrl}/vote`, data);
	return response.data;
};

export const getVotesByQuestion = async (questionId: string): Promise<GetVotesAPIResponse> => {
	const response = await baseApi.get(`${voteBaseUrl}/question/${questionId}`);
	return response.data;
};

export const getUserVote = async (questionId: string): Promise<BaseAPIResponse> => {
	const response = await baseApi.get(`${voteBaseUrl}/user/${questionId}`);
	return response.data;
};


